/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from "zustand"
import {
  CellId,
  DepartmentDutyRosterSpreadsheetContext,
  ModalState,
} from "./duty-roster-spreadsheet"
import { DateOnly } from "../../../../@client/System"
import dayjs from "dayjs"
import { match } from "ts-pattern"
import { useShallow } from "zustand/react/shallow"
import { useEffect, useRef } from "react"
import {
  DepartmentDutyRosterResult,
  DutySchedulingRowHeader,
  FormulaResult,
  OrchestraActivityDto,
  SimpleFormulaDto,
} from "@/orval/gen/model"

// Groups
type Day = { type: "day"; date: string; span: number; bg?: string }
type WeekDividerCol = { type: "week"; span: number; date: string }
type Month = { type: "month"; span: number; date: string }
type DayCol = {
  type: "day-col"
  date: DateOnly
  colId: number
  activity: OrchestraActivityDto | null
}
type FormulaCol = { type: "formula"; title: string }
type RowHeaderCol = { type: "row-header" }
type Column = DayCol | Day | WeekDividerCol | Month | FormulaCol | RowHeaderCol

type Window = {
  startIndex: number
  endIndex: number
  startRow: number
  endRow: number
  startCol: number
  endCol: number
  totalCols: number
  totalRows: number
}

type ScrollPosition = {
  left: number
  top: number
  width: number
  height: number
}

type RowHeader = Omit<DutySchedulingRowHeader, "children"> & {
  visible: boolean
  children: RowHeader[]
  rowIndex: number
}

type RowExpandedStates = Record<string, boolean>

type FormulaResults = Record<string, FormulaResult>

interface State {
  formulas: SimpleFormulaDto[] | []
  formulaResults: FormulaResults
  updateFormulaResults: (results: FormulaResults) => void
  headerColumnCount: number
  rowExpandedStates: Record<string, boolean>
  setRowExpandedStates: (
    v: RowExpandedStates | ((x: RowExpandedStates) => RowExpandedStates)
  ) => void
  colWidths: number[]
  window: Window
  scrollPosition: ScrollPosition
  setScrollPosition: (pos: ScrollPosition) => void
  dataUpdatedAt: number | null
  gridTemplateColumns: string
  rowHeaders: RowHeader[]
  columns: Column[]
  items: DepartmentDutyRosterResult | null
  setItems: (
    items: DepartmentDutyRosterResult,
    compact: boolean,
    dataUpdatedAt: number
  ) => void
  setContext: (ctx: DepartmentDutyRosterSpreadsheetContext) => void
  context: DepartmentDutyRosterSpreadsheetContext | null
  setSelected: (cellId: CellId | null) => void
  selected: CellId | null
  modalState: ModalState | null
  setModalState: (state: ModalState | null) => void
  getActivity: (cellId: CellId) => OrchestraActivityDto | null
  selectedQuickAddShift: {
    action?: "add" | "delete"
    kind: "presence" | "absence"
    canDirectAdd?: boolean
    addMode?: "direct" | "modal"
    startTime?: string | null
    endTime?: string | null
    shiftTypeId: number
    name: string
  } | null
  setSelectedQuickAddShift: (
    value: {
      action?: "add" | "delete"
      kind: "presence" | "absence"
      canDirectAdd?: boolean
      addMode?: "direct" | "modal"
      startTime?: string | null
      endTime?: string | null
      shiftTypeId: number
      name: string
    } | null
  ) => void
}

const colWith = (c: Column, compact: boolean) =>
  match(c)
    .with({ type: "day-col" }, (x) => (compact ? (x.colId === 0 ? 20 : 0) : 70))
    .with({ type: "day" }, () => (compact ? 0 : 4))
    .with({ type: "week" }, () => 0)
    .otherwise(() => 0)

const getColumns = (items: DepartmentDutyRosterResult): Column[] => {
  const columns: Column[] = []
  if (items.days?.length === 0) {
    return []
  }

  let offset = 2

  let currentWeekIndex = 0
  let currentMonth: Month = {
    type: "month",
    span: 0,
    date: items.days![0].date!,
  }

  let currentMonthIndex = 0
  let currentWeek: WeekDividerCol = {
    type: "week",
    span: 0,
    date: items.days![0].date!,
  }
  columns.push(currentMonth)
  columns.push(currentWeek)

  const length = items.days!.length
  for (let i = 0; i < length; i++) {
    const isLast = i == length - 1
    const day = items.days![i]
    const date = day!.date!

    // update current month and set next month
    if (dayjs(date).month() !== dayjs(currentMonth.date).month() || isLast) {
      let span = offset - currentMonthIndex + i
      currentMonth.span = span

      if (!isLast) {
        currentMonth = { type: "month", span: 0, date }
        columns.push(currentMonth)
      } else {
        currentMonth.span += 4
      }

      currentMonthIndex = offset + i
      offset++
    }

    // update current week and set next week
    if (
      dayjs(day.date).isoWeek() !== dayjs(currentWeek.date).isoWeek() ||
      isLast
    ) {
      let span = offset - currentWeekIndex + i
      currentWeek.span = span

      if (!isLast) {
        currentWeek = { type: "week", span: 0, date }
        columns.push(currentWeek)
      } else {
        currentWeek.span += 2
      }
      currentWeekIndex = offset + i

      offset++
    }

    columns.push({
      type: "day",
      span: day.span! + 1,
      date: date,
      bg: day.bg || undefined,
    })
    for (let x = 0; x < day.columns?.length!; x++) {
      columns.push({
        type: "day-col",
        date: date as DateOnly,
        colId: x,
        activity: day.columns![x].activity as any,
      })
      offset++
    }
  }
  return columns
}

function positionToWindow(
  left: number,
  top: number,
  viewportWidth: number,
  viewportHeight: number,
  cellWidths: number[], // array of widths per column
  cellHeight: number,
  totalCols: number,
  totalRows: number,
  rowHeaders: RowHeader[]
) {
  // Helper to find column index by horizontal scroll offset
  function findStartCol(offset: number): number {
    let acc = 0
    for (let i = 0; i < cellWidths.length; i++) {
      acc += cellWidths[i]
      if (acc > offset) return i
    }
    return cellWidths.length - 1
  }

  const startCol = findStartCol(left)
  let accWidth = 0
  let endCol = startCol
  for (let i = startCol; i < cellWidths.length; i++) {
    accWidth += cellWidths[i]
    if (accWidth >= viewportWidth) {
      endCol = i
      break
    }
  }
  if (accWidth < viewportWidth) {
    endCol = cellWidths.length - 1
  }
  if (endCol < startCol) {
    endCol = startCol
  }

  // Window rows must be based on actual rendered heights (visible rows are 20px, hidden rows are 0px).
  // Otherwise large collapsed trees can inflate the window and create excessive DOM nodes.
  const flattenRows = (headers: RowHeader[]): RowHeader[] => {
    const rows: RowHeader[] = []
    const walk = (header: RowHeader) => {
      rows.push(header)
      header.children.forEach(walk)
    }
    headers.forEach(walk)
    return rows
  }

  const flatRows = flattenRows(rowHeaders)
  const rowHeights = flatRows.map((row) => (row.visible ? cellHeight : 0))

  const findRowByOffset = (offset: number): number => {
    if (rowHeights.length === 0) {
      return 0
    }

    let acc = 0
    for (let i = 0; i < rowHeights.length; i++) {
      acc += rowHeights[i]
      if (acc > offset) {
        return i
      }
    }

    return rowHeights.length - 1
  }

  const startRow = findRowByOffset(top)
  const endRow = Math.min(totalRows - 1, findRowByOffset(top + viewportHeight))

  const startIndex = startRow * totalCols + startCol
  const endIndex = endRow * totalCols + endCol
  // TODO: parameterize
  const columnHeaderHeight = 5 // number of header rows

  const result = {
    startIndex,
    endIndex,
    startRow: startRow + columnHeaderHeight,
    endRow: endRow + columnHeaderHeight,
    startCol,
    endCol,
    totalCols,
    totalRows,
  }
  return result
}

const getWindow = (
  pos: ScrollPosition,
  items: DepartmentDutyRosterResult,
  colWidth: number[],
  rowHeaders: RowHeader[]
) =>
  positionToWindow(
    pos.left,
    pos.top,
    pos.width,
    pos.height,
    colWidth,
    20,
    items.colCount || 0,
    items.rowCount || 0,
    rowHeaders
  )

function addVisibility(v: DutySchedulingRowHeader, i: number): RowHeader {
  return {
    ...v,
    rowIndex: i,
    visible: true,
    children: v.children?.map((v, i2) => addVisibility(v, i2)) ?? [],
  }
}

const hideRec = (rowHeader: RowHeader): RowHeader => ({
  ...rowHeader,
  visible: false,
  children: rowHeader.children?.map(hideRec),
})

function updateVisibility(
  expandedState: RowExpandedStates,
  header: RowHeader,
  parentId: string | null
): RowHeader {
  const id = header.id?.toString()
  if (!id) return header
  if (parentId === null) {
    return {
      ...header,
      visible: true,
      children: header.children.map((v) =>
        updateVisibility(expandedState, v, id)
      ),
    }
  }
  if (expandedState[parentId] === true) {
    return {
      ...header,
      visible: true,
      children: header.children.map((v) =>
        updateVisibility(expandedState, v, `${parentId}/${id}`)
      ),
    }
  }
  if (expandedState[parentId] === false) {
    return hideRec(header)
  }
  return header
}

export const useGridStore = create<State>((set, get) => ({
  rowExpandedStates: {},
  setRowExpandedStates: (v) =>
    set((state) => {
      const nextState = typeof v === "function" ? v(state.rowExpandedStates) : v
      const rowHeaders = state.rowHeaders.map((v) =>
        updateVisibility(nextState, v, null)
      )
      const window = state.items
        ? getWindow(
            state.scrollPosition,
            state.items ?? [],
            state.colWidths,
            rowHeaders
          )
        : state.window

      return {
        ...state,
        rowHeaders,
        rowExpandedStates: nextState,
        window,
      }
    }),
  formulas: [],
  formulaResults: {},
  updateFormulaResults: (results: FormulaResults) =>
    set((state) => {
      if (state.formulaResults === results) {
        return state
      }
      return {
        ...state,
        formulaResults: results,
      }
    }),
  colWidths: [],
  headerColumnCount: 0,
  window: {
    startIndex: 0,
    length: 100,
    endIndex: 0,
    startRow: 0,
    endRow: 0,
    startCol: 0,
    endCol: 0,
    totalCols: 0,
    totalRows: 0,
  },
  scrollPosition: { left: 0, top: 0, width: 0, height: 0 },
  setScrollPosition: (pos) =>
    set((state) => {
      const samePos =
        state.scrollPosition.left === pos.left &&
        state.scrollPosition.top === pos.top &&
        state.scrollPosition.width === pos.width &&
        state.scrollPosition.height === pos.height

      if (samePos) {
        return state
      }

      const window = state.items
        ? getWindow(pos, state.items, state.colWidths, state.rowHeaders)
        : state.window

      const sameWindow =
        state.window.startIndex === window.startIndex &&
        state.window.endIndex === window.endIndex &&
        state.window.startRow === window.startRow &&
        state.window.endRow === window.endRow &&
        state.window.startCol === window.startCol &&
        state.window.endCol === window.endCol

      if (sameWindow) {
        return { ...state, scrollPosition: pos }
      }

      return { ...state, scrollPosition: pos, window }
    }),
  dataUpdatedAt: null,
  gridTemplateColumns: "",
  rowHeaders: [],
  columns: [],
  activitiesByCellId: {},
  modalState: null,
  setModalState: (modalState) => set((state) => ({ ...state, modalState })),
  selectedQuickAddShift: null,
  setSelectedQuickAddShift: (selectedQuickAddShift) =>
    set((state) => ({
      ...state,
      selectedQuickAddShift,
    })),
  items: null,
  selected: null,
  setSelected: (cellId) =>
    set((state) => {
      return {
        ...state,
        selected: cellId,
      }
    }),
  getActivity: (cellId: CellId) =>
    getActivity(cellId, get()?.items ?? ({ days: [] } as any)),
  setItems: (
    items: DepartmentDutyRosterResult,
    compact: boolean,
    updatedAt: number
  ) =>
    set((state) => {
      if (state.items === items && state.dataUpdatedAt === updatedAt) {
        return state
      }

      const formulaColumns =
        items.formulas?.map(
          (v) =>
            ({
              type: "formula",
              title: v.name ?? "",
              // tooltip: v.text ?? "",
            }) satisfies FormulaCol
        ) ?? []
      const c = getColumns(items!)
      const contentColOffset = 3 + formulaColumns.length

      // first three columns must/should depent on backed data
      const colWidths = [
        5,
        5,
        100,
        ...formulaColumns.map((_) => 25),
        ...c.map((v) => colWith(v, compact)),
      ]
      const widths = colWidths.map((v) => v + "px").join(" ")
      const rowHeaders = items.rowHeaders?.map(addVisibility) ?? []
      const window = state.items
        ? getWindow(state.scrollPosition, items, colWidths, rowHeaders)
        : state.window
      const result: State = {
        ...state,
        rowHeaders,
        headerColumnCount: contentColOffset,
        formulas: items.formulas ?? [],
        colWidths: colWidths,
        dataUpdatedAt: updatedAt,
        items,
        columns: c,
        gridTemplateColumns: widths,
        window,
      }
      return result
    }),
  setContext: (ctx: DepartmentDutyRosterSpreadsheetContext) =>
    set((state) => ({
      ...state,
      context: ctx,
    })),
  context: null,
}))

export const useGridStoreInitializer = ({
  data,
  compact,
  updatedAt,
}: {
  data: DepartmentDutyRosterResult | undefined
  compact: boolean
  updatedAt: number
}) => {
  const lastAppliedDataRef = useRef<DepartmentDutyRosterResult | undefined>(
    undefined
  )
  const lastAppliedCompactRef = useRef<boolean | null>(null)
  const lastAppliedUpdatedAtRef = useRef<number | null>(null)
  const store = useGridStore(
    useShallow((s) => ({
      setItems: s.setItems,
      updateFormulaResults: s.updateFormulaResults,
    }))
  )

  useEffect(() => {
    const shouldApply =
      Boolean(data) &&
      (lastAppliedDataRef.current !== data ||
        lastAppliedCompactRef.current !== compact ||
        lastAppliedUpdatedAtRef.current !== updatedAt)

    if (data && shouldApply) {
      store.setItems(data, compact, updatedAt)
      lastAppliedDataRef.current = data
      lastAppliedCompactRef.current = compact
      lastAppliedUpdatedAtRef.current = updatedAt
    }
  }, [data, compact, updatedAt, store.setItems])

  return {
    storeInitialized: Boolean(data),
    updateFormulaResults: store.updateFormulaResults,
  }
}

const getRowId = (cellId: CellId) => cellId.split("]")[0].substring(1)

const getColId = (cellId: CellId) => cellId.split("]")[1].substring(1)

export function getContractId(cellId: CellId) {
  return parseInt(getRowId(cellId).split("/")[2])
}

function getDay(cellId: CellId): DateOnly {
  return getColId(cellId).split("/")[0] as DateOnly
}

function getDayCol(cellId: CellId): number {
  return parseInt(getColId(cellId).split("/")[1])
}

export const getDepartmentId = (cellId: CellId) => {
  const raw = getRowId(cellId).split("/")[0]
  return parseInt(raw)
}

export const getWorkareaId = (cellId: CellId) => {
  const raw = getRowId(cellId).split("/")[1]
  return parseInt(raw)
}

const getActivity = (cellId: CellId, items: DepartmentDutyRosterResult) => {
  const d = getDay(cellId)
  const col = getDayCol(cellId)
  const day = items.days?.find((v) => v.date?.startsWith(d))
  return day?.columns![col]?.activity ?? null
}
